import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { useCallback, useEffect } from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'

import type { Location } from 'react-router-dom'

/**
 * @description Intercepts the back button on native platforms and calls the onGoBackHandler function
 * @param onGoBackHandler - The function to call when the back button is pressed or when navigating back in browser - return true if the back action should be blocked, false otherwise
 * @returns void
 */
export const useInterceptBackHandler = (onGoBackHandler: () => boolean) => {
  const navigate = useNavigate()

  useBlocker(
    useCallback(
      (tx: { currentLocation: Location; nextLocation: Location; historyAction: 'POP' | 'PUSH' | 'REPLACE' }) => {
        if (tx.historyAction === 'POP') {
          return onGoBackHandler()
        }
        return false
      },
      [onGoBackHandler],
    ),
  )

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    const listener = App.addListener('backButton', () => {
      const shouldBlock = onGoBackHandler()
      // if the back action should not be blocked, navigate back in the app
      if (shouldBlock === false) {
        navigate(-1)
      }
    })

    return () => {
      listener.then((listener) => listener.remove())
    }
  }, [onGoBackHandler, navigate])
}
